// packages/ui/src/components/GeneralDialog.tsx
import type { ReactNode } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, useTheme, Grid,
} from '@mui/material';
import { CloseButton } from '@tourtask/ui/components/CloseButton';
import type { TFunc } from '@tourtask/types/i18n';

type BreakpointToken = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/** An explicit header background + foreground pair — see `GeneralDialogProps.headerColors`. */
export interface DialogHeaderColors {
  backgroundColor: string;
  color: string;
}

/** Where a click on the backdrop landed, in viewport px: the click's own `clientX` / `clientY`. */
export interface BackdropClick {
  clientX: number;
  clientY: number;
}

/** The position of the click MUI hands `onClose` with a `backdropClick` (a mouse event, which MUI types as `{}`). */
function backdropClickOf(event: unknown): BackdropClick | null {
  if (typeof event !== 'object' || event === null || !('clientX' in event) || !('clientY' in event)) return null;
  const { clientX, clientY } = event;
  return typeof clientX === 'number' && typeof clientY === 'number' ? { clientX, clientY } : null;
}

interface GeneralDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Secondary line under the title (e.g. the entity the dialog is about). Only shown with a title. */
  subtitle?: ReactNode;
  type?: GeneralDialogType;
  /** Accept a breakpoint token, a number/string, or a responsive map */
  width?: number | string | Partial<Record<BreakpointToken, number | string>>;
  maxWidth?: false | BreakpointToken; // optional escape hatch
  fullWidth?: boolean;
  /** When true, the user must explicitly choose an action: backdrop click, ESC, and the close button are all disabled. */
  forceDecision?: boolean;
  /**
   * Explicit header colours, replacing the `type`-derived semantic pair.
   *
   * For a dialog that must carry a TENANT's own branding rather than a status
   * colour — the embed storefront's neutral dialogs open on top of a header the
   * business picked, and a stock `info` blue reads as someone else's chrome.
   * BOTH values are supplied because the business chooses its foreground too,
   * so it cannot be derived with `getContrastText`.
   *
   * Leave it unset for a status dialog: a `warning` or `error` that inherits a
   * brand colour has lost the thing it was communicating.
   */
  headerColors?: DialogHeaderColors;
  /**
   * Translator override forwarded to the embedded `CloseButton`. Required in
   * non-Next.js contexts (e.g. embed) where there is no
   * `NextIntlClientProvider`.
   */
  /**
   * Narrowed to the ONE key this dialog's subtree needs (`Close`, via CloseButton).
   *
   * Not the default `TFunc`: the marketing site hands this a `public` translator and the
   * backoffice a `common` one, and a function is contravariant in its parameter, so
   * neither is assignable to the other's full key union. Stating the actual requirement
   * makes both work — and `Close` exists in both catalogs, which is what makes it true.
   */
  t?: TFunc<'Close'>;
  /**
   * Stacking layer, when it must differ from MUI's modal layer
   * (`theme.zIndex.modal`) — to sit above something the caller itself places
   * just over another open modal.
   */
  zIndex?: number;
  /**
   * Takes over the backdrop: a click on it calls this INSTEAD of `onClose`,
   * with where it landed, and the caller decides what the click does. For a
   * dialog opened by a click on something its backdrop now covers (the second
   * click of a double-click lands on the backdrop), or opened over another
   * dialog that a click outside both is meant for. Escape and the close button
   * still call `onClose`.
   */
  onBackdropClick?: (click: BackdropClick) => void;
  /**
   * Once the dialog has closed and its exit animation has ended: where the focus is moved to the user's next step
   * (focused before then, it would be taken back by the dialog's own return of the focus).
   */
  onExited?: () => void;
  children?: ReactNode;
}

export type GeneralDialogType = 'success' | 'error' | 'warning' | 'info';

export function GeneralDialog({
  isOpen,
  title,
  subtitle,
  onClose,
  type = 'info',
  width = 'sm',
  maxWidth,
  fullWidth,
  forceDecision,
  headerColors,
  t,
  zIndex,
  onBackdropClick,
  onExited,
  children
}: GeneralDialogProps) {
  const theme = useTheme();

  const header = headerColors ?? {
    success: { backgroundColor: theme.palette.success.main, color: theme.palette.getContrastText(theme.palette.success.main) },
    error:   { backgroundColor: theme.palette.error.main,   color: theme.palette.getContrastText(theme.palette.error.main) },
    warning: { backgroundColor: theme.palette.warning.main, color: theme.palette.getContrastText(theme.palette.warning.main) },
    info:    { backgroundColor: theme.palette.info.main,    color: theme.palette.getContrastText(theme.palette.info.main) },
  }[type];

  const bp = theme.breakpoints.values; // { xs:0, sm:600, md:900, lg:1200, xl:1536 }
  const tokenToPx = (t: BreakpointToken) => (t === 'xs' ? 360 : bp[t]);

  // Build responsive width — keys are breakpoint tokens, values are
  // either a CSS length string or a pixel number.
  let responsiveWidth: Partial<Record<BreakpointToken, number | string>> = {
    xs: 'min(100vw - 32px, 100%)',
  };

  if (typeof width === 'string') {
    const tokens: ReadonlyArray<BreakpointToken> = ['xs', 'sm', 'md', 'lg', 'xl'];
    const isToken = (tokens as ReadonlyArray<string>).includes(width);
    const val = isToken ? tokenToPx(width as BreakpointToken) : width;
    responsiveWidth = { xs: 'min(100vw - 32px, 100%)', sm: val };
  } else if (typeof width === 'number') {
    responsiveWidth = { xs: 'min(100vw - 32px, 100%)', sm: width };
  } else if (width && typeof width === 'object') {
    responsiveWidth = { xs: 'min(100vw - 32px, 100%)', ...width };
  }

  return (
    <Dialog
      open={isOpen}
      onClose={(event, reason) => {
        if (forceDecision && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
        if (reason === 'backdropClick' && onBackdropClick !== undefined) {
          const click = backdropClickOf(event);
          // Always a mouse event; were one ever without a position, the
          // backdrop would close the dialog as it does without the hook.
          if (click !== null) {
            onBackdropClick(click);
            return;
          }
        }
        onClose();
      }}
      disableEscapeKeyDown={forceDecision}
      maxWidth={maxWidth ?? false}   // disable cap unless caller opts in
      fullWidth={fullWidth}
      sx={zIndex === undefined ? undefined : { zIndex }}
      slotProps={{ paper: { sx: { width: responsiveWidth } }, transition: onExited === undefined ? undefined : { onExited } }}
    >
      {title && (
        <DialogTitle style={header}>
          <Grid container justifyContent="space-between" alignItems="center" wrap="nowrap">
            <Grid>
              {subtitle ? (
                // Spans, not divs: DialogTitle renders an <h2>.
                <Box component="span" sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                  <Box component="span">{title}</Box>
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, mt: 0.25, fontSize: '1rem', fontWeight: 400, opacity: 0.9 }}
                  >
                    {subtitle}
                  </Box>
                </Box>
              ) : title}
            </Grid>
            {!forceDecision && <Grid><CloseButton handleClose={onClose} t={t} /></Grid>}
          </Grid>
        </DialogTitle>
      )}
      <DialogContent dividers>{children}</DialogContent>
    </Dialog>
  );
}
